import {FC, useState} from "react";
import {TextInput} from "@interstate/components/TextInput";
import {Button} from "@interstate/components/Button";
import {PlusIcon} from "@interstate/components/Icons";
import {BoidAssignmentInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {BoidTypeSelect} from "./BoidTypeSelect";

export interface CustomBoidCreationRowProps {
  entityId: string;
  onSubmit: (data: BoidAssignmentInput) => void;
  isLoading?: boolean;
}

export const CustomBoidCreationRow: FC<CustomBoidCreationRowProps> = ({
  entityId,
  onSubmit,
  isLoading = false
}) => {
  const [type, setType] = useState("");
  const [boidId, setBoidId] = useState("");
  const [typeError, setTypeError] = useState<string | null>(null);
  const [idError, setIdError] = useState<string | null>(null);

  const handleSubmit = () => {
    let hasError = false;

    if (!type.trim()) {
      setTypeError("BOID Type is required");
      hasError = true;
    } else {
      setTypeError(null);
    }

    if (!boidId.trim()) {
      setIdError("BOID ID is required");
      hasError = true;
    } else {
      setIdError(null);
    }

    if (hasError) return;

    onSubmit({
      businessOperationType: type,
      businessOperationId: boidId,
      commonOrgId: entityId
    });

    // Reset form after submit
    setType("");
    setBoidId("");
    setTypeError(null);
    setIdError(null);
  };

  return (
    <div
      data-testid="custom-boid-creation-row"
      style={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-end",
        padding: "12px",
        backgroundColor: "var(--ids-sem-color-surface-layer-1, #f9f9f9)",
        borderRadius: "4px",
        marginBottom: "12px"
      }}
    >
      <div style={{flex: 1}}>
        <BoidTypeSelect
          id="custom-boid-type"
          value={type}
          onChange={e => {
            setType(e.target.value);
            if (typeError) setTypeError(null);
          }}
          disabled={isLoading}
        />
        {typeError && (
          <div data-testid="custom-boid-type-error" style={{color: "red", fontSize: "12px", marginTop: "4px"}}>
            {typeError}
          </div>
        )}
      </div>

      <div style={{flex: 1}}>
        <TextInput
          id="custom-boid-id"
          data-testid="custom-boid-id"
          name="custom-boid-id"
          label="BOID ID"
          value={boidId}
          onChange={e => {
            setBoidId(e.target.value);
            if (idError) setIdError(null);
          }}
          disabled={isLoading}
          placeholder="Enter BOID ID"
        />
        {idError && (
          <div data-testid="custom-boid-id-error" style={{color: "red", fontSize: "12px", marginTop: "4px"}}>
            {idError}
          </div>
        )}
      </div>

      <Button
        id="custom-boid-submit"
        data-testid="custom-boid-submit"
        buttonStyle="primary"
        size="small"
        onClick={handleSubmit}
        disabled={isLoading}
        isLoading={isLoading}
        startIcon={!isLoading ? <PlusIcon /> : undefined}
      >
        Add
      </Button>
    </div>
  );
};
